/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { Order, TableStatus } from "@lemmonhq/types";
import { formatPrice, getOrderDisplayName } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";

/**
 * Presentational only — everything comes in as props. It deliberately does not
 * call `useAuth`/`useShop`: the parent already holds both, and injecting them
 * again from this leaf failed at runtime inside the modal's slot.
 */
export type TableOrdersSummaryProps = {
  tableStatus?: TableStatus;
  shopOrders: Order[];
  currency: string;
  /** When given, each timeline card becomes a button opening that order. */
  onOrderClick?: (orderId: number, orderName: string) => void;
};

type TableOrder = TableStatus["orders"][number];

const STATUS_BADGE: Record<string, string> = {
  new: "badge-info",
  preparing: "badge-warning",
  ready: "badge-success",
};

const STATUS_DOT: Record<string, string> = {
  new: "bg-info",
  preparing: "bg-warning",
  ready: "bg-success",
};

const formatTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString("fr-CH", {
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
  const isToday = date.toDateString() === new Date().toDateString();
  if (isToday) return time;
  const day = date.toLocaleDateString("fr-CH", {
    day: "numeric",
    month: "short",
  });
  return `${day}, ${time}`;
};

/**
 * Fills the panel that `CoursesFinalizer` occupies when "Suite" is on, so the
 * cashier can see what is already on the table they are about to send a cart
 * to. Reads only from the `TableStatus` the modal has already fetched — no
 * extra request. `TableStatus.orders` carries no per-order amounts, so the
 * money figure is the table total.
 */
export const TableOrdersSummary = defineComponent<TableOrdersSummaryProps>({
  name: "TableOrdersSummary",
  props: ["tableStatus", "shopOrders", "currency", "onOrderClick"],
  setup(props) {
    const { t } = useLocalization();

    /**
     * `TableStatus.orders` carries no `user`, `table_id` or `service_method`,
     * so `getOrderDisplayName` cannot read it directly — it would fall through
     * to the `WEB-` branch for every staff order. Resolve the full order from
     * the shop cache and fall back to the raw display id when it is not there.
     *
     * Already sorted oldest-first by `get_tables.sql`, which is the order the
     * timeline wants.
     */
    const orders = computed(() =>
      (props.tableStatus?.orders || []).map((order: TableOrder) => {
        const full = props.shopOrders.find(
          (candidate: Order) => candidate.id === order.id,
        );
        return {
          ...order,
          name: full ? getOrderDisplayName(full) : `#${order.display_id}`,
        };
      }),
    );

    return () => {
      // No table picked yet.
      if (!props.tableStatus) {
        return (
          <div class="flex flex-1 flex-col items-center justify-center gap-1 px-6 py-6 text-center">
            <span class="material-symbols-outlined text-4xl text-base-content/25">
              table_restaurant
            </span>
            <p class="max-w-[15rem] text-sm text-base-content/50">
              {t("pos.tables.select_table_hint")}
            </p>
          </div>
        );
      }

      // Picked, but nothing open on it.
      if (orders.value.length === 0) {
        return (
          <div class="flex flex-1 flex-col items-center justify-center gap-1 px-6 text-center py-6">
            <div class="flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <span class="material-symbols-outlined text-3xl text-success">
                check_circle
              </span>
            </div>
            <div class="space-y-1">
              <p class="font-medium text-base-content">
                {t("pos.tables.table_free")}
              </p>
              <p class="text-sm text-base-content/50">
                {t("pos.tables.table_free_hint")}
              </p>
            </div>
          </div>
        );
      }

      return (
        <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
          <div class="flex items-center gap-2">
            <span class="material-symbols-outlined text-base text-base-content/55">
              receipt_long
            </span>
            <span class="text-xs font-semibold uppercase tracking-wider text-base-content/55">
              {t("pos.tables.on_this_table")}
            </span>
          </div>

          {/* Aggregate */}
          <div class="grid grid-cols-2 gap-2">
            <div class="rounded-xl border bg-base-200 px-3 py-2">
              <div class="text-xl font-semibold text-accent">
                {orders.value.length}
              </div>
              <div class="text-xs text-base-content/55">
                {t("pos.cart.active_orders")}
              </div>
            </div>
            <div class="rounded-xl border bg-base-200 px-3 py-2">
              <div class="text-xl font-semibold text-accent">
                {formatPrice(
                  props.tableStatus.total_amount || 0,
                  true,
                  2,
                  props.currency,
                )}
              </div>
              <div class="text-xs text-base-content/55">
                {t("pos.tables.table_total")}
              </div>
            </div>
          </div>

          {props.tableStatus.party?.name ? (
            <div class="flex items-center gap-1.5 text-sm text-base-content/70">
              <span class="material-symbols-outlined text-base text-base-content/55">
                group
              </span>
              <span class="truncate">{props.tableStatus.party.name}</span>
            </div>
          ) : null}

          {/* Timeline, oldest first */}
          <div class="min-h-0 flex-1 overflow-y-auto pr-4">
            <ol class="relative flex flex-col gap-4">
              {orders.value.map((order, index) => (
                <li key={order.id} class="relative flex gap-3 items-center">
                  {/* Gutter: stretches to this row's full height (the card's
                      height) so the connecting line is centered on the dot
                      regardless of card height, and bridges the `gap-4`
                      between rows via the negative bottom offset below —
                      an absolutely positioned line can't reach past its own
                      row into that gap otherwise. */}
                  <div class="relative flex w-2.5 shrink-0 flex-col items-center justify-center self-stretch">
                    {index > 0 ? (
                      <span
                        aria-hidden="true"
                        class="absolute left-1/2 top-0 bottom-1/2 w-px -translate-x-1/2 bg-accent/10"
                      />
                    ) : null}
                    <span
                      aria-hidden="true"
                      class={`relative z-10 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ring-base-100 ${
                        STATUS_DOT[order.status] || "bg-base-content/30"
                      }`}
                    />
                    {index < orders.value.length - 1 ? (
                      <span
                        aria-hidden="true"
                        class="absolute left-1/2 top-1/2 -bottom-4 w-px -translate-x-1/2 bg-accent/10"
                      />
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={!props.onOrderClick}
                    onClick={() => props.onOrderClick?.(order.id, order.name)}
                    class={`flex min-w-0 flex-1 items-center justify-between gap-2 border bg-base-300 rounded p-2 text-left ${
                      props.onOrderClick
                        ? "cursor-pointer transition-colors hover:bg-base-200"
                        : "cursor-default"
                    }`}
                  >
                    <div class="flex flex-col min-w-0 items-start">
                      <span class="truncate font-medium text-accent">
                        {order.name}
                      </span>
                      <span class="text-xs text-left text-base-content/50">
                        {formatTime(order.created_at)}
                      </span>
                    </div>
                    <span
                      class={`badge badge-sm shrink-0 ${
                        STATUS_BADGE[order.status] || "badge-ghost"
                      }`}
                    >
                      {t(`orders.ticket_status.${order.status}`)}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      );
    };
  },
});
